import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import {Button, Col, Row, Table} from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import {CSVLink} from 'react-csv';
import {TableSkeleton} from '../../shared/ui/LoadingSkeletons';
import {fetchEdgeAddress, fetchMeterReport} from './services/meterReportApi';

const csvHeaders = [
  {label: 'Zähler', key: 'name'},
  {label: 'Seriennummern', key: 'serialNumbers'},
  {label: 'Tarif 1 Startwert [kWh]', key: 't1StartValue'},
  {label: 'Tarif 1 Startzeit', key: 't1StartTStamp'},
  {label: 'Tarif 2 Startwert [kWh]', key: 't2StartValue'},
  {label: 'Tarif 2 Startzeit', key: 't2StartTStamp'},
  {label: 'Tarif 1 Endwert [kWh]', key: 't1StopValue'},
  {label: 'Tarif 1 Endzeit', key: 't1StopTStamp'},
  {label: 'Tarif 2 Endwert [kWh]', key: 't2StopValue'},
  {label: 'Tarif 2 Endzeit', key: 't2StopTStamp'},
  {label: 'Tarif 1 Verbrauch in Auswertungsperiode [kWh]', key: 't1Difference'},
  {label: 'Tarif 2 Verbrauch in Auswertungsperiode [kWh]', key: 't2Difference'},
];

const transformReportData = (rawData) =>
  Object.keys(rawData).map((k) => ({
    name: k,
    serialNumbers: rawData[k].serialNumbers.join(','),
    t1StartValue: rawData[k].t1.start.value.toFixed(3),
    t1StartTStamp: rawData[k].t1.start.tStamp,
    t2StartValue: rawData[k].t2.start.value.toFixed(3),
    t2StartTStamp: rawData[k].t2.start.tStamp,
    t1StopValue: rawData[k].t1.stop.value.toFixed(3),
    t1StopTStamp: rawData[k].t1.stop.tStamp,
    t2StopValue: rawData[k].t2.stop.value.toFixed(3),
    t2StopTStamp: rawData[k].t2.stop.tStamp,
    t1Difference: (rawData[k].t1.stop.value - rawData[k].t1.start.value).toFixed(3),
    t2Difference: (rawData[k].t2.stop.value - rawData[k].t2.start.value).toFixed(3),
  }));

const MeterReport = () => {
  const {eid} = useParams();
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState({start: undefined, end: undefined});
  const [start, setStart] = useState();
  const [end, setEnd] = useState();
  const [data, setData] = useState();
  const [address, setAddress] = useState('');
  const [filename, setFilename] = useState('');

  const generateReport = async () => {
    setLoading(true);
    try {
      const addr = await fetchEdgeAddress(eid);
      setAddress(addr);

      const startISO = new Date(selectedPeriod.start).toISOString();
      const endDate = new Date(selectedPeriod.end);
      endDate.setHours(23, 59, 59);
      const endISO = endDate.toISOString();

      setStart(startISO);
      setEnd(endISO);
      setFilename(`${eid}_${moment(startISO).format('DD.MM.YYYY')}-${moment(endISO).format('DD.MM.YYYY')}.csv`);

      const rawData = await fetchMeterReport(eid, startISO, endISO);
      setData(transformReportData(rawData));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h4>Meter Evaluation</h4>
      <div className="island p-4">
        <Row className="d-flex align-items-end">
          <Col>
            Startdate:{' '}
            <DatePicker
              selected={selectedPeriod.start}
              onChange={(date) => setSelectedPeriod({...selectedPeriod, start: date})}
            />
          </Col>
          <Col>
            Enddate:{' '}
            <DatePicker
              selected={selectedPeriod.end}
              onChange={(date) => setSelectedPeriod({...selectedPeriod, end: date})}
            />
          </Col>
          <Col>
            <Button
              variant="primary"
              disabled={!selectedPeriod.start || !selectedPeriod.end || loading}
              className="mb-0"
              onClick={generateReport}
            >
              Generate Report
            </Button>
          </Col>
        </Row>
      </div>
      <br />
      {loading ? (
        <TableSkeleton rows={5} columns={2} />
      ) : (
        data && (
          <>
            <div className="island p-4">
              <Table>
                <colgroup>
                  <col style={{width: '50%'}} />
                  <col style={{width: '50%'}} />
                </colgroup>
                <tbody>
                  <tr>
                    <th>Object</th>
                    <td className="text-end">{address}</td>
                  </tr>
                  <tr>
                    <th>Timerange</th>
                    <td className="text-end">
                      {moment(start).format('DD.MM.YYYY')} - {moment(end).format('DD.MM.YYYY')}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
            <br />
            <div className="island p-0" style={{overflowX: 'auto'}}>
              <Table className="island border-0" responsive hover>
                <thead>
                  <tr>
                    <th rowSpan="2" style={{width: '150px'}}>Meter</th>
                    <th colSpan="2" style={{width: '200px'}}>Reading Start Date</th>
                    <th colSpan="2" style={{width: '200px'}}>Reading End Date</th>
                    <th colSpan="2" style={{width: '200px'}}>Consumption in Evaluation Period</th>
                  </tr>
                  <tr>
                    <th style={{width: '110px'}}>Tarif 1 [kWh]</th>
                    <th style={{width: '110px'}}>Tarif 2 [kWh]</th>
                    <th style={{width: '110px'}}>Tarif 1 [kWh]</th>
                    <th style={{width: '110px'}}>Tarif 2 [kWh]</th>
                    <th style={{width: '110px'}}>Tarif 1 [kWh]</th>
                    <th style={{width: '110px'}}>Tarif 2 [kWh]</th>
                  </tr>
                </thead>
                <tbody style={{fontSize: '0.875rem'}}>
                  {data.map((d) => (
                    <tr key={d.name}>
                      <td>{d.name}</td>
                      <td>{d.t1StartValue}</td>
                      <td>{d.t2StartValue}</td>
                      <td>{d.t1StopValue}</td>
                      <td>{d.t2StopValue}</td>
                      <td>{d.t1Difference}</td>
                      <td>{d.t2Difference}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <CSVLink
              data={data}
              filename={filename}
              className="btn btn-link float-end"
              target="_blank"
              headers={csvHeaders}
            >
              Export CSV
            </CSVLink>
          </>
        )
      )}
    </>
  );
};

export default MeterReport;
